import { formatDataManagerAuditFieldLabel } from "./dataManagerAuditLabels.js";
import { getAssignmentTripCount } from "./resultsFleetData.js";
import { buildApplicationFeedbackItems, feedbackDurationLabel } from "./sourceChangeApplicationFeedback.js";

const HARD_BLOCKERS = new Set([
  "route_published", "route_executed", "route_frozen", "interday_preload_present",
  "publication_context_unavailable",
  "end_of_day_present", "multi_trip_not_supported", "truck_assignment_missing",
]);
const CAPACITY_REASONS = new Set(["load_capacity_exceeded", "capacity_insufficient", "compartment_capacity_insufficient"]);
const DURATION_REASONS = new Set(["max_work_hours_exceeded", "time_insufficient", "strict_time_limit_exceeded"]);
const ADJUST_THEN_REFRESH_GUIDANCE = "Ajustez la tournée, puis cliquez sur Actualiser.";
const reasons = (detail) => Array.isArray(detail?.reasonCodes) ? detail.reasonCodes : [];
const reasonAlias = (code) => ({
  time_insufficient: "max_work_hours_exceeded",
  strict_time_limit_exceeded: "max_work_hours_exceeded",
  capacity_insufficient: "load_capacity_exceeded",
  compartment_capacity_insufficient: "load_capacity_exceeded",
}[code] || code);

function preferredTruckConstraint(detail) {
  const lastChangedField = String(detail?.lastChangedField || detail?.leadingChangedField || "").trim();
  if (lastChangedField === "max_work_hours") return "duration";
  if (lastChangedField === "capacity_tons" || lastChangedField === "compartments") return "capacity";
  const kind = detail?.recovery?.constraintKind || detail?.recovery?.constraint_kind;
  if (kind === "load_capacity") return "capacity";
  if (kind === "work_duration") return "duration";
  const codes = reasons(detail).map(reasonAlias);
  if (codes.some((code) => DURATION_REASONS.has(code))) return "duration";
  if (codes.some((code) => CAPACITY_REASONS.has(code))) return "capacity";
  return "";
}

function itemMatchesConstraint(item, constraint) {
  const code = reasonAlias(item?.reasonCode);
  if (constraint === "duration") return DURATION_REASONS.has(code);
  if (constraint === "capacity") return CAPACITY_REASONS.has(code);
  return false;
}

function rankTruckPresentationItems(items, detail) {
  const preferred = preferredTruckConstraint(detail);
  if (!preferred || !Array.isArray(items) || items.length < 2) return items;
  return [
    ...items.filter((item) => itemMatchesConstraint(item, preferred)),
    ...items.filter((item) => !itemMatchesConstraint(item, preferred)),
  ];
}

// Sections de Mise à jour : les champs de chaque onglet du Data Manager (Camions, Remorques, Chauffeurs). Le type se
// change dans Camions. Paramètres système, par partie concernée : la pause dîner au chauffeur, le débit d'aspiration à
// la remorque (avec l'aspirateur), les temps fixes au camion.
const DRIVER_SOURCE_FIELDS = new Set(["start_time", "end_time", "max_work_hours", "driver_user_id", "lunch_time",
  "lunch_break_minutes"]);
const TRAILER_SOURCE_FIELDS = new Set(["capacity_tons", "compartments", "aspirator", "aspirator_service_minutes",
  "aspirator_rate_tons_per_hour", "preferred_grain_type", "accepted_grain_types", "is_initial_preload_enabled",
  "evening_radius_km", "tags", "loading_rate_tons_per_hour", "unloading_rate_tons_per_hour"]);
export const TRUCK_SOURCE_CHANGE_SECTIONS = [
  { key: "truck", label: "Camions" },
  { key: "trailer", label: "Remorques" },
  { key: "driver", label: "Chauffeurs" },
];
const SECTION_RANK = { truck: 0, trailer: 1, driver: 2 };

export const truckSourceChangeFieldSection = (field) => (DRIVER_SOURCE_FIELDS.has(field) ? "driver"
  : TRAILER_SOURCE_FIELDS.has(field) ? "trailer" : "truck");

// Une seule carte par camion : la section du menu d'attelage utilisé dans le Gantt (« Changer de remorque » ou
// « Changer de camion » ; une ligne quittée par son camion, Camions), sinon la première section touchée (Camions, puis
// Remorques, puis Chauffeurs). Un dompeur, réglé entièrement dans Camions, y reste.
export function truckSourceChangeSection(detail) {
  if (detail?.dompeur) return "truck";
  if (detail?.couplingChange) return detail.couplingChange.change === "trailer" ? "trailer" : "truck";
  const ranks = (Array.isArray(detail?.changedFields) ? detail.changedFields : [])
    .map((field) => SECTION_RANK[truckSourceChangeFieldSection(field?.field)]);
  return ranks.length ? TRUCK_SOURCE_CHANGE_SECTIONS[Math.min(...ranks)].key : "truck";
}

export function groupTruckSourceChangesBySection(details) {
  const list = Array.isArray(details) ? details : [];
  return TRUCK_SOURCE_CHANGE_SECTIONS
    .map((section) => ({ ...section, details: list.filter((detail) => truckSourceChangeSection(detail) === section.key) }))
    .filter((section) => section.details.length > 0);
}

// « Mise à jour > Chauffeurs » pour les cartes données ; sans carte connue, la section Camions.
export function formatTruckSourceChangesMenuPath(details) {
  const keys = new Set((Array.isArray(details) ? details : []).filter(Boolean).map(truckSourceChangeSection));
  const labels = TRUCK_SOURCE_CHANGE_SECTIONS.filter((section) => keys.has(section.key)).map((section) => section.label);
  return `Mise à jour > ${labels.join(" ou ") || "Camions"}`;
}

export function canRefreshTruckSourceChange(detail) {
  if (reasons(detail).some((code) => HARD_BLOCKERS.has(code))) return false;
  return detail?.status === "actionable" || (detail?.status === "blocked" && detail?.retryable === true);
}

// Cartes actualisées sans clic, d'après la ligne du Gantt affiché : toutes celles d'un camion sans voyage,
// et les cartes Chauffeurs (heures) d'un camion sans chauffeur. Une carte déjà refusée reste dans Mise à jour.
export function selectAutomaticTruckSourceChanges(details, assignments) {
  const rows = new Map();
  for (const row of Array.isArray(assignments) ? assignments : []) {
    const truckId = String(row?.truck_id ?? "").trim();
    if (truckId && !rows.has(truckId)) rows.set(truckId, row);
  }
  return (Array.isArray(details) ? details : []).filter((detail) => {
    const row = rows.get(detail?.truckId);
    const fields = Array.isArray(detail?.changedFields) ? detail.changedFields : [];
    if (!row || detail.status !== "actionable" || detail.applicationFeedback || !canRefreshTruckSourceChange(detail)
      || detail.recovery?.status === "confirmation_required") return false;
    return getAssignmentTripCount(row) === 0 || (!String(row.driver_name ?? "").trim() && fields.length > 0
      && fields.every((field) => truckSourceChangeFieldSection(field?.field) === "driver"));
  });
}

export function getTruckSourceChangePresentation(detail, options = {}) {
  const items = rankTruckPresentationItems(
    buildApplicationFeedbackItems(detail?.applicationFeedback, options),
    detail,
  );
  const hardReason = reasons(detail).find((code) => HARD_BLOCKERS.has(code));
  // A current route restriction takes precedence over any earlier solver failure.
  const hardItem = items.find((item) => HARD_BLOCKERS.has(item.reasonCode));
  if (hardItem) items.splice(0, 0, ...items.splice(items.indexOf(hardItem), 1));
  const canRefresh = !hardItem && canRefreshTruckSourceChange(detail);
  const codes = [...reasons(detail), ...items.map((item) => item.reasonCode)];
  const primaryCode = reasonAlias(items[0]?.reasonCode);
  const capacity = CAPACITY_REASONS.has(primaryCode);
  const duration = DURATION_REASONS.has(primaryCode);
  const radius = codes.includes("evening_radius_exceeded");
  const business = detail?.applicationFeedback?.kind === "business" || detail?.recovery || capacity || duration || radius
    || codes.some((code) => CAPACITY_REASONS.has(reasonAlias(code)) || DURATION_REASONS.has(reasonAlias(code)));
  let label = "Bloqué";
  let guidance = "";
  if (hardReason || hardItem) {
    label = "Bloqué";
    // Camion publié, figé ou avec un Chargé PM : Actualiser n'y touche pas ; il garde ses anciens temps jusqu'à un choix.
    if (["route_published", "route_executed", "route_frozen", "end_of_day_present"].includes(hardReason || hardItem?.reasonCode)) {
      guidance = "Actualiser ne modifie pas ce camion : rejetez le changement ou utilisez Optimiser. D’ici là, il garde ses anciens temps.";
    }
  } else if (detail?.status === "unsupported") {
    label = "Non pris en charge";
    guidance = codes.some((code) => ["global_period_change", "field_not_supported_locally", "structural_truck_change"].includes(code))
      ? "Utilisez Optimiser pour appliquer cette modification."
      : "Cette modification n’est pas prise en charge par Actualiser.";
  } else if (detail?.status === "actionable") {
    label = "À actualiser";
  } else if (canRefresh) {
    label = business ? "À ajuster" : "À vérifier";
    guidance = codes.includes("product_preference_conflict")
      ? "Déplacez ces commandes vers un camion associé à leur produit, puis cliquez sur Actualiser."
      : radius ? "Retirez la commande concernée de la tournée, puis cliquez sur Actualiser."
      : capacity ? "Réaffectez une partie du chargement, puis cliquez sur Actualiser."
      : duration || business ? ADJUST_THEN_REFRESH_GUIDANCE
        : "Cliquez sur Actualiser pour vérifier à nouveau ce camion.";
  }
  return { label, canRefresh, guidance, items };
}

const numberLabel = (value) => new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 3 }).format(value);
const finite = (value) => typeof value === "number" && Number.isFinite(value);

// Changement d'attelage : seule la remorque ou le camion remplacé est décrit ; les conflits suivent. Une ligne quittée
// par son camion dit où il est parti, au lieu de « Actif : Oui → Non ».
export function formatCouplingChangeLine(coupling) {
  if (coupling?.change === "departed") return `Camion parti sur une autre remorque : ${coupling.tractor} → ${coupling.trailer}`;
  const trailer = coupling?.change === "trailer";
  const current = trailer ? coupling.trailer : coupling?.tractor;
  return `${trailer ? "Remorque" : "Camion"} : ${coupling?.previous ? `${coupling.previous} → ` : ""}${current}`;
}

export function compactTruckDiagnostic(item) {
  if (!item) return "";
  if (finite(item.plannedValue) && finite(item.limit)) {
    if (item.unit === "tons") {
      const excess = item.plannedValue - item.limit;
      const excessLabel = excess > 0 ? ` (+${numberLabel(excess)} t)` : "";
      return `${item.label || "Chargement"} : ${numberLabel(item.plannedValue)} t / ${numberLabel(item.limit)} t${excessLabel}.`;
    }
    if (item.unit === "minutes") {
      const flex = item.flex_minutes ?? item.flexMinutes;
      const label = (item.duration_basis || item.durationBasis) === "retained_route" ? "Tournée" : "Durée calculée";
      return `${label} : ${feedbackDurationLabel(item.plannedValue)} / ${feedbackDurationLabel(item.limit)}${item.limit_basis === "remaining_budget" ? " restantes" : " disponibles"}${finite(flex) && flex > 0 ? `, dont ${feedbackDurationLabel(flex)} de souplesse` : ""}.${item.strict_time_limits && item.overtime_authorized ? " Limite stricte du recalcul." : ""}`;
    }
  }
  const orderIds = [...new Set(item.orderIds || [])].filter((id) => !item.message?.includes(id));
  return orderIds.length ? `${orderIds.join(", ")} : ${item.message}` : item.message;
}

function formatTruckSourceFieldLine(field, formatValue, dispatchPeriod) {
  if (!field) return "";
  const label = field.field === "max_work_hours" && dispatchPeriod === "evening" ? "Heures max Soir"
    : field.field === "start_time" && dispatchPeriod === "evening" ? "Début Soir"
      : field.field === "evening_radius_km" ? "Rayon Soir"
      : formatDataManagerAuditFieldLabel(field.field, { fallbackLabel: field.label });
  let value;
  if (field.valueVisibility === "redacted") {
    value = "Valeur masquée";
  } else if (field.field === "compartments") {
    const before = Array.isArray(field.before) ? field.before : [];
    const after = Array.isArray(field.after) ? field.after : [];
    const total = (items) => items.reduce((sum, item) => sum + Number(item.capacity_tons), 0);
    value = `${before.length} → ${after.length} · ${numberLabel(total(before))} → ${numberLabel(total(after))} t`;
  } else {
    const unit = field.field === "max_work_hours" ? " h" : field.field === "evening_radius_km" ? " km" : "";
    value = `${formatValue(field.field, field.before)} → ${formatValue(field.field, field.after)}${unit}`;
  }
  return `${label} : ${value}`;
}

export function summarizeTruckSourceFieldLines(fields, formatValue, { preferredField = "", dispatchPeriod = "day", groupBySource = false } = {}) {
  let visible = (Array.isArray(fields) ? fields : []).filter((field) => !["truck_id", "axle_count"].includes(field?.field));
  if (groupBySource) {
    // Cartes de Mise à jour : lignes du camion, puis de la remorque, puis du chauffeur ; le champ préféré reste en tête.
    visible = visible.map((field, index) => ({ field, index, rank: SECTION_RANK[truckSourceChangeFieldSection(field?.field)] }))
      .sort((left, right) => left.rank - right.rank || left.index - right.index)
      .map(({ field }) => field);
  }
  const preferred = String(preferredField || "").trim();
  const ordered = preferred
    ? [...visible.filter((field) => field?.field === preferred), ...visible.filter((field) => field?.field !== preferred)]
    : visible;
  return ordered.map((field) => formatTruckSourceFieldLine(field, formatValue, dispatchPeriod)).filter(Boolean);
}

export function summarizeTruckSourceFields(fields, formatValue, options = {}) {
  const lines = summarizeTruckSourceFieldLines(fields, formatValue, options);
  return lines[0] || "Modification de configuration";
}

export const keepTruckUnitsTogether = (value) => value.replace(/\b\d+(?:[.,]\d+)?\s+(?:h(?:\s+\d{1,2}(?!\d))?|min|km|t)\b/g,
  (unit) => unit.replace(/\s/g, "\u00a0"));

// Infobulle du camion : valeurs actuelles (remorque, camion attelé, chauffeur) que Mise à jour n'a pas encore appliquées à la tournée.
const PENDING_HOVER_FIELDS = new Set(["capacity_tons", "max_work_hours", "start_time"]);
export function withPendingTruckSourceValues(assignment, truckChanges) {
  const truckId = String(assignment?.truck_id ?? "").trim();
  const detail = truckId && (Array.isArray(truckChanges?.details) ? truckChanges.details : [])
    .find((item) => String(item?.truckId ?? item?.truck_id ?? "").trim() === truckId);
  const fields = (detail?.changedFields || detail?.changed_fields || []).filter((field) => PENDING_HOVER_FIELDS.has(field?.field));
  return fields.length ? { ...assignment, ...Object.fromEntries(fields.map((field) => [field.field, field.after])) } : assignment;
}

const SOURCE_LINE_LEAD_SEPARATOR = " : ";

export function splitTruckSourceLineLead(value) {
  const text = String(value || "");
  const index = text.indexOf(SOURCE_LINE_LEAD_SEPARATOR);
  if (index <= 0) return { lead: "", rest: text };
  return {
    lead: text.slice(0, index),
    rest: text.slice(index + SOURCE_LINE_LEAD_SEPARATOR.length),
  };
}
